import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import moment from 'moment';
import addressFormat from 'address-format';
import Dropzone from 'react-dropzone';
import { APP_DATE_FORMAT } from '../../config/app_config';
import HomeLogoUpload from './HomeLogoUpload';
import DialogForm from '../shared/DialogForm';

const UPLOAD_COMPANY_LOGO = 'uploadCompanyLogo';
const REMOVE_COMPANY_LOGO = 'removeCompanyLogo';

export default class HomeIndex extends React.Component {
  static propTypes = {
    account: PropTypes.object.isRequired,
    authentication: PropTypes.object.isRequired,
    clearFileFunction: PropTypes.func.isRequired,
    dialog: PropTypes.object.isRequired,
    handleOnDrop: PropTypes.func.isRequired,
    handleLogoUpload: PropTypes.func.isRequired,
    imgFile: PropTypes.object.isRequired,
    isProviderAdmin: PropTypes.bool.isRequired,
    primaryContact: PropTypes.object.isRequired,
    removeCompanyLogoFunction: PropTypes.func.isRequired,
    toggleDialogFunction: PropTypes.func.isRequired,
  };

  handleLocalDrop = file => {
    const { handleOnDrop } = this.props;
    handleOnDrop(file);
  };

  formatAddress = account => {
    const formattedAddress = addressFormat({
      address: account.address1,
      address2: account.address2,
      city: account.city,
      subdivision: account.stateProvince,
      postalCode: account.postcode,
      countryCode: 'US', // For now default to US
    });

    return formattedAddress;
  };

  renderCompanyInfo = (account, defaultBlank, isProviderAdmin, toggleDialogFunction) => {
    const { companyName } = account;
    let address = defaultBlank;
    if (account.address1) {
      address = this.formatAddress(account);
    }

    const contractStartDate = moment(account.createdOn).format(APP_DATE_FORMAT);
    const phone = account.phone || defaultBlank;

    return (
      <div className="card-border">
        <div className="card-header">
          <div className="card-header-title">Company</div>
        </div>
        <div className="card-block">
          <dl className="row">
            <dt className="col-sm-3">Name</dt>
            <dd className="col-sm-9">{companyName}</dd>

            <dt className="col-sm-3">Address</dt>
            <dd className="col-sm-9">{address}</dd>

            <dt className="col-sm-3">Phone</dt>
            <dd className="col-sm-9">{phone}</dd>

            <dt className="col-sm-3">Member Since</dt>
            <dd className="col-sm-9">{contractStartDate}</dd>

            <dt className="col-sm-3">Logo</dt>
            <dd className="col-sm-9">
              <HomeLogoUpload
                account={account}
                isProviderAdmin={isProviderAdmin}
                toggleDialogFunction={toggleDialogFunction}
              />
            </dd>
          </dl>
        </div>
      </div>
    );
  };

  renderUserInfo = user => {
    const { email, phone } = user;
    const userName = `${user.firstName} ${user.lastName}`;

    return (
      <div className="card-border">
        <div className="card-header">
          <div className="card-header-title">Primary Contact</div>
        </div>
        <div className="card-block">
          {email ? (
            <dl className="row">
              <dt className="col-sm-3">Name</dt>
              <dd className="col-sm-9">{userName}</dd>

              <dt className="col-sm-3">Email</dt>
              <dd className="col-sm-9">{email}</dd>

              <dt className="col-sm-3">Phone</dt>
              <dd className="col-sm-9">{phone}</dd>
            </dl>
          ) : (
            <p>A primary contact has not been set.</p>
          )}
        </div>
      </div>
    );
  };

  renderLoginLink = () => (
    <div className="col-sm-12">
      <p>
        You have no session token at this time. Please <Link to="/account/login">Log In</Link> to
        fix that.
      </p>
    </div>
  );

  renderUploadMessage = () => {
    const { imgFile, clearFileFunction } = this.props;

    if (!imgFile.name) {
      return (
        <div className="small-text">
          {imgFile.message ? <p className="text-danger">{imgFile.message}</p> : null}
          <p>
            Upload a logo image to appear in your Datarista emails to clients.
            <br />
            Accepted formats: .jpg, .png, .svg.
            <br />
            Max size: 5Mb.
          </p>
        </div>
      );
    }

    return (
      <div>
        <h4>File Selected</h4>
        <ul>
          <li>
            {imgFile.name} - {imgFile.size} bytes &nbsp;
            <button
              type="button"
              className="close float-none"
              aria-label="Close"
              onClick={() => {
                clearFileFunction();
              }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </li>
        </ul>
      </div>
    );
  };

  renderRemoveDialog = () => {
    const { dialog, toggleDialogFunction, removeCompanyLogoFunction } = this.props;

    const dialogContent = (
      <div className="container">
        <p>Are you sure you would like to remove your company&rsquo;s logo image?</p>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            data-dismiss="modal"
            onClick={() => {
              toggleDialogFunction(dialog.id);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              removeCompanyLogoFunction();
            }}
          >
            Remove
          </button>
        </div>
      </div>
    );

    return (
      <DialogForm
        cancelFunction={toggleDialogFunction}
        content={dialogContent}
        id={REMOVE_COMPANY_LOGO}
        show={dialog.show}
        title="Remove Company Logo"
      />
    );
  };

  renderUploadDialog = () => {
    const { dialog, handleLogoUpload, imgFile, toggleDialogFunction } = this.props;
    const dialogContent = (
      <div className="container">
        <Dropzone className="logo-dropzone" onDrop={this.handleLocalDrop} multiple={false}>
          <p>Click to upload your logo, or drag and drop your logo here.</p>
        </Dropzone>
        <br />
        {this.renderUploadMessage()}
        <br />
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            data-dismiss="modal"
            onClick={() => {
              toggleDialogFunction(dialog.id);
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleLogoUpload}
            disabled={!imgFile.name || imgFile.message}
          >
            Upload
          </button>
        </div>
      </div>
    );

    return (
      <DialogForm
        cancelFunction={toggleDialogFunction}
        content={dialogContent}
        id={UPLOAD_COMPANY_LOGO}
        show={dialog.show}
        title="Upload Company Logo"
      />
    );
  };

  render() {
    const {
      authentication,
      account,
      dialog,
      isProviderAdmin,
      primaryContact,
      toggleDialogFunction,
    } = this.props;
    const { token } = authentication;
    const defaultBlank = 'not specified';

    return (
      <div className="modal-wrap">
        <div className="container-fluid container-home">
          <div className="header">
            <div className="header-body">
              <div className="row">
                <div className="col-sm-12">
                  <h1 className="header-title">Home</h1>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-sm-6">
              {token
                ? this.renderCompanyInfo(
                    account,
                    defaultBlank,
                    isProviderAdmin,
                    toggleDialogFunction
                  )
                : this.renderLoginLink()}
              {token ? this.renderUserInfo(primaryContact) : null}
            </div>
          </div>
        </div>
        {/* token ? <HomeNotifications account={account} /> : null */}

        {dialog.id === UPLOAD_COMPANY_LOGO && dialog.show ? this.renderUploadDialog() : null}
        {dialog.id === REMOVE_COMPANY_LOGO && dialog.show ? this.renderRemoveDialog() : null}
      </div>
    );
  }
}
